import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';

import const_env from '../app/constants/env';
import { CREATE_REFERRAL } from '../app/graphql/mutations';
import { GET_USER_LINKED } from '../app/graphql/queries';
import { groceryListIdSelector } from '../app/groceryList/groceryList.slice';
import { userLinkedDataHandler } from '../app/homepage/homepage.thunkActions';
import { UseGetFlag } from '../app/launchDarkly';
import { useAppSelector } from '../app/store/hooks';
import { useUpAuth } from '../components/authentication/UpAuth';
import {
  TextHuge,
  TextMid,
  TextMidBold,
  TextPrimary,
  TextPrimaryLight,
  TextSmall,
  TextSmaller,
  TextSmallerBold,
} from '../components/typography';
import UserPreference from '../components/userPreference/UserPreference';
import useLogRecipeEdit from '../hooks/useLogRecipeEdit';
import { GroceryListIcon } from '../iconRef/groceryListIcons';
import { FavoriteIconFilled } from '../iconRef/ProfileIcons';
import { MyCreationsIcon } from '../iconRef/profilePageIcons';
import { ReferralIcon } from '../iconRef/referralIcons';
import { ReactComponent as ContactIcon } from '../svg/contactIcon.svg';
import { ReactComponent as CookingIcon } from '../svg/cooking.svg';
import { ReactComponent as CopyIcon } from '../svg/copyIcon2.svg';
import { ReactComponent as InfoRedIcon } from '../svg/infoRedIcon.svg';
import { ReactComponent as InstallIcon } from '../svg/installIcon.svg';
import { ReactComponent as LogoutIcon } from '../svg/logoutIcon.svg';
import { ReactComponent as ReferralLinkIcon } from '../svg/referralLinkIcon.svg';
import { ErrorManagementContext } from '../utils/errorManagement/ErrorManagementProvider';
import { EventLogsEnum } from '../utils/events';
import { logEvents } from '../utils/logging';
import { isStandaloneMode } from '../utils/os.utils';
import { urlOpen } from './ExternalLinkUtil';
import styles from './profilePage.module.css';
import { ExternalLinksData } from './profilePage.types';

const ExternalLinks = ({
  title,
  image,
  url,
}: {
  title: string;
  image: string;
  url: string;
}) => {
  const user = useAppSelector((state) => state.main.user);
  return (
    <Grid
      className={styles.profilePageButton}
      onClick={() => urlOpen(`${url}/?user=${user?.id}`)}
    >
      <img alt="coins" className={styles.coins_image} src={image} />
      <TextPrimary color="#000000" text={title} />
    </Grid>
  );
};

const ProfilePage = ({
  handleInstallClick,
  fullscreenFlag,
}: {
  handleInstallClick: any;
  fullscreenFlag: boolean;
}) => {
  const user = useAppSelector((state) => state.main.user);
  const device = useAppSelector((state) => state.main.device);
  const [open, setOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [referralLink, setReferralLink] = useState('');
  const [referralId, setReferralId] = useState('');
  const { cookClubLog } = useContext(ErrorManagementContext);
  const [userLinked, setUserLinked] = useState(false);
  const sessionId: any = sessionStorage.getItem('sessionId');
  const { logout } = useUpAuth();
  const { mobExternalLink, requiresProvisioning } = UseGetFlag();
  const history = useHistory();
  const { logRecipeEdit } = useLogRecipeEdit();
  const { referralString } = UseGetFlag();
  const groceryListId = useAppSelector(groceryListIdSelector);

  useQuery(GET_USER_LINKED, {
    variables: { userId: user?.id },
    onCompleted: (data) => {
      const isUserLinked = userLinkedDataHandler(data, cookClubLog);
      if (isUserLinked) setUserLinked(isUserLinked);
    },
  });

  const [insertReferralId] = useMutation(CREATE_REFERRAL, {
    onError: (err) => {
      cookClubLog(
        `Fectching User Linked Devices Failed`,
        'ERROR_ON_ACCESS_USER_LINKED_DEVICES',
        { err },
        { ui: { type: 'error' }, browserConsole: true }
      );
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleReferralClose = () => {
    setReferralOpen(false);
  };

  // Todo : we should handle undefined, since we don' t want the user to redirect to undefined, hence add a todo to add a check before setting the link or handle error case
  const handleReferralOpen = async () => {
    const referralIdTemp = await insertReferralId({
      variables: {
        create_by: user?.id,
        expires_at: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
      },
    });

    if (referralIdTemp?.data?.insert_refferal_link_one?.id) {
      logEvents(sessionId, 'APP_REFERRAL_GENERATE', {
        userId: user?.id,
        deviceId: device?.displayName ?? device?._id,
        eventData: {
          referralId: referralIdTemp?.data?.insert_refferal_link_one?.id,
        },
      });
      setReferralOpen(true);
      setReferralId(referralIdTemp?.data?.insert_refferal_link_one?.id);
      setReferralLink(
        `${const_env.SHOP_BASE_URL}/${referralIdTemp?.data?.insert_refferal_link_one?.id}`
      );
    }
  };

  const signOutHandler = () => {
    logout();
  };

  function SnackbarText() {
    cookClubLog('', 'Copied referral link successfully!', '', {
      ui: { type: 'info' },
      browserConsole: true,
    });
  }
  const copyLink = () => {
    const text = `${referralString} ${referralLink}`;
    navigator.clipboard.writeText(text).then(
      () => {
        logEvents(sessionId, 'APP_REFERRAL_COPY', {
          userId: user?.id,
          deviceId: device?.displayName ?? device?._id,
          eventData: { referralId },
        });
        SnackbarText();
      },
      (err) => {
        console.log(err);
      }
    );
  };

  const openFavorites = () => {
    if (user?.id) {
      const url = `/favorites/?User_ID=${user.id}`;
      history.push(url);
      logEvents('null', EventLogsEnum.APP_FAVOURITES_OPENED);
    }
  };
  const openCookingHistory = () => {
    if (user?.id) {
      const url = `/cooking-history`;
      history.push(url);
      logRecipeEdit(EventLogsEnum.APP_HISTORY_PAGE_OPENED, {
        userId: user?.id,
      });
    }
  };

  const openGroceryList = () => {
    logRecipeEdit(EventLogsEnum.VIEW_GROCERY_LIST, {
      eventData: {
        list_id: groceryListId,
      },
    });
    if (user?.id) {
      const url = `/grocery-list`;
      history.push(url);
    }
  };

  const rateUpliance = () => {
    if (user?.id) {
      const url = `/account/view-rate-upliance`;
      history.push(url);

      logEvents('null', 'REFERRAL_REVIEW_PAGE_OPENED', {
        userId: user?.id,
      });
      // logRecipeEdit(EventLogsEnum.APP_HISTORY_PAGE_OPENED, {
      //     userId: user?.id,
      // });
    }
  };

  const handleMyCreationsCLick = () =>
    history.push(
      `/filter/?created_by=${user?.id}&title=My Creations&source=my creations`
    );
  const openBluetoothSettings = () => {
    if (user?.id) {
      const url = `/bluetooth-connection`;
      history.push(url);
    }
  };

  const handlePhoneCall = () => {
    window.location.href = 'tel:+918904962940';
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box className={styles.container}>
      <Box className={styles.profile_page_header_container}>
        <TextMid color="#fff" text="My Profile" />
        <Box className={styles.user_detail_row}>
          <Box
            className={styles.user_profile_image}
            component="img"
            height={50}
            src={user?.photoURL}
          />
          <Box className={styles.user_detail_card}>
            <TextPrimaryLight color="#fff" text="Hello" />
            <TextHuge color="#fff" text={user?.nickName ?? ''} />
          </Box>
        </Box>
      </Box>
      <Grid className={styles.profile_section_container}>
        <Grid className={styles.profilePageButton} onClick={openCookingHistory}>
          <CookingIcon />
          <TextPrimary text="Cooking History" />
        </Grid>
        <Grid className={styles.profilePageButton} onClick={openGroceryList}>
          <GroceryListIcon />
          <TextPrimary text="Grocery List" />
        </Grid>
        <Grid className={styles.profilePageButton} onClick={openFavorites}>
          <FavoriteIconFilled />
          <TextPrimary text="Favorites" />
        </Grid>
        {requiresProvisioning && (
          <Grid
            className={styles.profilePageButton}
            onClick={openBluetoothSettings}
          >
            <BluetoothIcon />
            <TextPrimary text="BLE Provision" />
          </Grid>
        )}
        <Grid
          className={styles.profilePageButton}
          onClick={handleMyCreationsCLick}
        >
          <MyCreationsIcon />
          <TextPrimary text="My Creations" />
        </Grid>
        <UserPreference />
        {/* {!fullscreenFlag && !isStandaloneMode && (
          <Grid
            className={styles.profilePageButton}
            onClick={handleInstallClick}
          >
            <InstallIcon />
            <TextPrimary text="Install App" />
          </Grid>
        )} */}

        <Grid className={styles.profilePageButton} onClick={handlePhoneCall}>
          <ContactIcon />
          <TextPrimary color="#000" text="Contact Us" />
        </Grid>

        {/* {userLinked && referralString ? (
          <Grid className={styles.profilePageButton} onClick={rateUpliance}>
            <ReferralIcon />
            <TextPrimary text="My Referral & Review" />
          </Grid>
        ) : null} */}
        {mobExternalLink?.map((data: ExternalLinksData) => (
          <ExternalLinks image={data.icon} title={data.title} url={data.url} />
        ))}
        <Grid className={styles.profilePageButton} onClick={handleClickOpen}>
          <LogoutIcon />
          <TextPrimary text="Log Out" />
        </Grid>
      </Grid>
      <Dialog
        className={styles.log_out_dialog_container}
        fullWidth
        onClose={handleClose}
        open={open}
      >
        <Box className={styles.log_out_dialog}>
          <TextMid text="Are you sure you want to" />
          <TextMid text="log out?" />
          <Box className={styles.log_out_action_container}>
            <Box className={styles.log_out_btn} onClick={signOutHandler}>
              <TextMid text="Logout" />
            </Box>
            <Box className={styles.log_out_cancel_btn} onClick={handleClose}>
              <TextMid color="#fff" text="Cancel" />
            </Box>
          </Box>
        </Box>
      </Dialog>
      <Dialog fullWidth onClose={handleReferralClose} open={referralOpen}>
        <Box
          sx={{
            mx: 2,
            mt: 4,
            mb: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#D4C6FF',
              height: '50px',
              width: '50px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: '-55px',
              position: 'fixed',
            }}
          >
            <ReferralLinkIcon />
          </Box>
          <Box sx={{ mt: 1 }}>
            <TextMidBold text="Save a Friend" />
          </Box>
          <Box sx={{ textAlign: 'center !important' }}>
            <TextSmall text="A delishUp⤴️ for them and goodies for you!" />
          </Box>
          <TextField
            disabled
            id="outlined-basic"
            inputProps={{
              style: { fontSize: '14px', width: '68vw', padding: '10px' },
            }}
            label=""
            style={{
              backgroundColor: '#E7E7E7',
              borderRadius: '10px',
            }}
            sx={{
              '& fieldset': { border: 'none' },
              mt: 3,
            }}
            value={referralLink}
            variant="outlined"
          />
          <Box
            onClick={copyLink}
            sx={{
              display: 'flex',
              backgroundColor: '#3B1F8E',
              alignItems: 'center',
              px: 2,
              py: 1,
              gap: 1,
              borderRadius: '10px',
              width: 'fit-content',
              mt: 2,
            }}
          >
            <CopyIcon />
            <TextSmall color="#fff" text="Copy" />
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              gap: 1,
              borderRadius: '10px',
              width: 'fit-content',
            }}
          >
            <InfoRedIcon />
            <TextSmallerBold
              color="#FF0000"
              text="This link is valid for 24 hours only"
            />
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;
